import { imageRefusal } from "@scheduler/shared";
import type { ReportImageType } from "@scheduler/shared";
import { useEffect, useRef, useState, type HTMLAttributes } from "react";

import { imageFiles, shrink, toBase64, type PendingImage } from "./reportImages";

/**
 * Attaching screenshots to a problem report (3 Nov 2026), the way Canopy does it
 * (its components/ImageAttach.tsx): paste, drop or Add image, each checked and shrunk, shown as
 * thumbnails with an X, and sent with the report in one entry. Nothing here SHOWS a stored
 * image: the scheduler's reports are read on Canopy's admin page.
 */

export interface PendingImages {
  readonly pending: readonly PendingImage[];
  /** How many are still being shrunk: counted against the five, and sending waits for them. */
  readonly shrinking: number;
  readonly add: (files: readonly File[]) => Promise<void>;
  readonly remove: (key: string) => void;
  readonly clear: () => void;
  /** As the API takes them. */
  readonly payload: () => Promise<{ contentType: ReportImageType; data: string }[]>;
  /** Spread on the element around the text box: paste and drop go to `add`. */
  readonly dropTarget: Pick<HTMLAttributes<HTMLElement>, "onPaste" | "onDragOver" | "onDrop">;
}

export function usePendingImages(onError: (message: string | null) => void): PendingImages {
  const [pending, setPending] = useState<PendingImage[]>([]);
  const [shrinking, setShrinking] = useState(0);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  // Thumbnails are object URLs; let them go with the component.
  useEffect(() => () => pendingRef.current.forEach((i) => URL.revokeObjectURL(i.url)), []);

  const add = async (files: readonly File[]) => {
    onError(null);
    let count = pendingRef.current.length + shrinking;
    for (const file of files) {
      const refused = imageRefusal(file, count);
      if (refused) {
        onError(refused);
        break;
      }
      count += 1;
      setShrinking((n) => n + 1);
      try {
        const { contentType, blob } = await shrink(file);
        const image: PendingImage = { key: crypto.randomUUID(), contentType, blob, url: URL.createObjectURL(blob) };
        setPending((current) => [...current, image]);
      } catch (e) {
        onError(e instanceof Error ? e.message : String(e));
      } finally {
        setShrinking((n) => n - 1);
      }
    }
  };

  const remove = (key: string) =>
    setPending((current) => {
      current.filter((i) => i.key === key).forEach((i) => URL.revokeObjectURL(i.url));
      return current.filter((i) => i.key !== key);
    });

  const clear = () => {
    pendingRef.current.forEach((i) => URL.revokeObjectURL(i.url));
    setPending([]);
  };

  const payload = () =>
    Promise.all(pendingRef.current.map(async (i) => ({ contentType: i.contentType, data: await toBase64(i.blob) })));

  const dropTarget: PendingImages["dropTarget"] = {
    // A paste that also carries TEXT is left to the box: copying from Word or a web page puts a
    // picture of the text on the clipboard too, and that is not an image anybody meant to attach.
    onPaste: (e) => {
      if (e.clipboardData.types.includes("text/plain")) return;
      const files = imageFiles(e.clipboardData.items);
      if (files.length === 0) return;
      e.preventDefault();
      void add(files);
    },
    onDragOver: (e) => {
      if (e.dataTransfer.types.includes("Files")) e.preventDefault();
    },
    onDrop: (e) => {
      const files = imageFiles(e.dataTransfer.files);
      if (files.length === 0) return;
      e.preventDefault();
      void add(files);
    },
  };

  return { pending, shrinking, add, remove, clear, payload, dropTarget };
}

/** The thumbnails waiting to go, each with an X. Nothing while there are none. */
export function PendingThumbnails({ images }: { images: PendingImages }) {
  if (images.pending.length === 0 && images.shrinking === 0) return null;
  return (
    <div className="report-images">
      {images.pending.map((i) => (
        <span key={i.key} className="report-image">
          <img src={i.url} alt="" />
          <button type="button" className="report-image-remove" aria-label="Remove image" onClick={() => images.remove(i.key)}>
            ✕
          </button>
        </span>
      ))}
      {images.shrinking > 0 ? <span className="muted small">…</span> : null}
    </div>
  );
}

/** "Add image": picks from files -- on a phone, its photos or the camera. */
export function AddImageButton({ images, disabled }: { images: PendingImages; disabled?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          void images.add(imageFiles(e.target.files));
          e.target.value = "";
        }}
      />
      <button type="button" className="ghost" onClick={() => input.current?.click()} disabled={disabled}>
        Add image
      </button>
    </>
  );
}
